import React, { useCallback, useEffect, useState, useRef } from "react";
import './horizonTable.css';
import "../forms/themeCheckbox.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  adminEditLabelStyle,
  adminEditInputStyle,
  parseSiteRegisterDate,
  formatSiteRegisterDate,
  siteRegisterDatePickerProps,
  adminEditModalOverlayStyle,
  adminEditModalContentStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
} from "./adminEditModalTheme";
import { toast } from "react-toastify";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
function UkpnAdditionalConcernsPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const generatingPdfRef = useRef(false);

  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  const handleEdit = (form) => {
    setEditForm({
      ...form,
      formUuid: ensureFormUuid(form?.formUuid),
      materialsRequired: Array.isArray(form?.materialsRequired) ? form.materialsRequired : [],
      supportingPhotos: Array.isArray(form?.supportingPhotos) ? form.supportingPhotos : [],
    });
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => {
      const next = { ...prev, [field]: value };
      // Dependent fields only apply when the answer is YES - clear them otherwise
      // so stale data is never saved against a "NO" answer.
      if (field === 'otherContractorsOnSite' && value !== 'YES') {
        next.contractorContactDetails = "";
      }
      if (field === 'supportingPhotosTaken' && value !== 'YES') {
        next.supportingPhotos = [];
      }
      return next;
    });
  };

  // ---- Supporting Photos (shown only when "Have Supporting Photo's Been Taken" = YES) ----
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    files.forEach((file) => {
      if (!file.type || !file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setEditForm(prev => ({
          ...prev,
          supportingPhotos: [...(Array.isArray(prev.supportingPhotos) ? prev.supportingPhotos : []), reader.result],
        }));
      };
      reader.onerror = () => toast.error(`Failed to read ${file.name}`);
      reader.readAsDataURL(file);
    });
    // allow re-selecting the same file again
    e.target.value = "";
  };
  const removePhoto = (idx) => {
    setEditForm(prev => ({
      ...prev,
      supportingPhotos: (Array.isArray(prev.supportingPhotos) ? prev.supportingPhotos : []).filter((_, i) => i !== idx),
    }));
  };

  // ---- Materials Required (Description / Quantity) helpers ----
  const updateMaterialRow = (idx, field, value) => {
    setEditForm(prev => {
      const rows = Array.isArray(prev.materialsRequired) ? [...prev.materialsRequired] : [];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...prev, materialsRequired: rows };
    });
  };
  const addMaterialRow = () => {
    setEditForm(prev => ({
      ...prev,
      materialsRequired: [...(Array.isArray(prev.materialsRequired) ? prev.materialsRequired : []), { description: "", quantity: "" }],
    }));
  };
  const removeMaterialRow = (idx) => {
    setEditForm(prev => ({
      ...prev,
      materialsRequired: (Array.isArray(prev.materialsRequired) ? prev.materialsRequired : []).filter((_, i) => i !== idx),
    }));
  };

  const handleEditSave = async () => {
    // Basic validation
    if (!editForm.siteLocation || !editForm.engineer || !editForm.date) {
      toast.error("Site / Location, Engineer and Date are required");
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");

      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === editForm.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';

      const updatedForm = {
        ...editForm,
        formUuid: ensureFormUuid(editForm.formUuid),
        addedByFirstName,
        addedByLastName,
      };

      const res = await fetch(baseUrl + `/api/ukpn-additional-concerns/${editForm._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(updatedForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setForms(forms.map(f => f._id === editForm._id ? { ...f, ...updatedForm } : f));
        setShowEditModal(false);
        toast.success("Updated successfully");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const fetchForms = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/ukpn-additional-concerns", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setForms(data.data);
      } else {
        setForms([]);
      }
    } catch {
      setForms([]);
    }
  }, []);

  useEffect(() => {
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchForms();
    fetchAppUsers();
  }, [fetchForms]);

  const filtered = forms.filter(f => {
    const fields = [f.siteLocation, f.engineer, f.jobReference, f.date].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/ukpn-additional-concerns/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Deleted successfully!");
        await fetchForms();
      } else if (res.status === 404) {
        await fetchForms();
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const siteNameForUpload = (sharePointUploadRecord?.siteLocation || '').trim();

    if (!siteNameForUpload) {
      toast.error('Site name is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: siteNameForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  const generatePDF = async (record, options = {}) => {
    // Prevent duplicate calls
    if (generatingPdfRef.current) {
      return;
    }
    generatingPdfRef.current = true;

    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? {
      phaseType: options.phaseType || 'non-phase',
      phaseNumber: options.phaseNumber || '',
      network: options.network || '',
      siteName: options.siteName || '',
      onSuccess: options.onSuccess,
      onError: options.onError
    } : null;

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Form body is plain black on white; the branded header band stays.
      const M = 15;                    // page margin
      const contentW = pageWidth - M * 2;
      const primaryColor = [139, 22, 52]; // #8b1634
      const headerHeight = 30;

      // ---- Branded maroon header band with logo + title ----
      const drawHeader = () => {
        return new Promise((resolve) => {
          doc.setFillColor(...primaryColor);
          doc.setDrawColor(...primaryColor);
          doc.setLineWidth(0.5);
          doc.rect(0, 0, pageWidth, headerHeight, 'FD');

          const drawHeaderText = () => {
            const titleX = 90;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.setTextColor(255, 255, 255);
            doc.text("Additional Concerns Worksheet for Quotes", titleX, 13);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(255, 255, 255);
            doc.text("TTSL ref: SQE/PRO/019J", titleX, 19);
          };

          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = function () {
            try {
              const logoWidth = 70;
              const aspectRatio = logoImg.height / logoImg.width;
              const logoHeight = logoWidth * aspectRatio;
              const logoX = 10;
              const logoY = (headerHeight - logoHeight) / 2;

              const canvas = document.createElement('canvas');
              const scale = 2.5;
              canvas.width = logoImg.width * scale;
              canvas.height = logoImg.height * scale;
              const ctx = canvas.getContext('2d');
              ctx.scale(scale, scale);
              ctx.drawImage(logoImg, 0, 0);

              const highResDataUrl = canvas.toDataURL('image/png', 1.0);
              doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
            } catch (error) {
              doc.setFillColor(255, 255, 255);
              doc.rect(10, 6, 60, 15, 'F');
              doc.setFont("helvetica", "bold");
              doc.setFontSize(10);
              doc.setTextColor(0, 0, 0);
              doc.text("TAYLOR TECHNOLOGY", 15, 12);
              doc.text("SYSTEMS LIMITED", 15, 17);
            }
            drawHeaderText();
            resolve();
          };
          logoImg.onerror = function () {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
            drawHeaderText();
            resolve();
          };
          logoImg.src = onsiteproLogo;
        });
      };

      // ---- Bold + underlined heading, exactly like the form's labels ----
      const drawUnderlinedBold = (text, x, y, size = 10, maxW = contentW - 10) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(size);
        doc.setTextColor(0, 0, 0);
        const lines = doc.splitTextToSize(String(text), maxW);
        let yy = y;
        lines.forEach((ln) => {
          doc.text(ln, x, yy);
          const w = doc.getTextWidth(ln);
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.3);
          doc.line(x, yy + 1.2, x + w, yy + 1.2);
          yy += size * 0.52;
        });
        return yy;
      };

      // ---- Ruled answer lines (writes the value onto the form's lines) ----
      const drawRuledValue = (value, x, y, w, minLines) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        const txt = String(value || "").trim();
        const lines = txt ? doc.splitTextToSize(txt, w - 4) : [];
        const count = Math.max(lines.length, minLines);
        let yy = y;
        for (let i = 0; i < count; i++) {
          if (lines[i]) doc.text(lines[i], x + 2, yy);
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.4);
          doc.line(x, yy + 1.8, x + w, yy + 1.8);
          yy += 7;
        }
        return yy;
      };

      // ---- YES / NO tick boxes ----
      const drawTick = (bx, by) => {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.8);
        doc.line(bx + 1.2, by + 2.9, bx + 2.3, by + 4.3);
        doc.line(bx + 2.3, by + 4.3, bx + 4.4, by + 1.2);
      };
      const drawYesNo = (label, value, x, y, size = 10) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(size);
        doc.setTextColor(0, 0, 0);
        doc.text(label, x, y);
        const lw = doc.getTextWidth(label);
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(x, y + 1.2, x + lw, y + 1.2);

        const v = String(value || "").trim().toUpperCase();
        let cx = x + lw + 5;

        doc.text("YES", cx, y);
        cx += doc.getTextWidth("YES") + 2;
        doc.setLineWidth(0.5);
        doc.rect(cx, y - 4.6, 5.6, 5.6, 'S');
        if (v === 'YES') drawTick(cx, y - 4.6);
        cx += 5.6 + 5;

        doc.setFont("helvetica", "bold");
        doc.text("NO", cx, y);
        cx += doc.getTextWidth("NO") + 2;
        doc.setLineWidth(0.5);
        doc.rect(cx, y - 4.6, 5.6, 5.6, 'S');
        if (v === 'NO') drawTick(cx, y - 4.6);

        return y + 9;
      };

      // =====================  PAGE 1  =====================
      await drawHeader();

      // ---- Site / Location box (left) ----
      const infoTop = headerHeight + 12;
      const leftW = 95;
      const leftH = 42;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.6);
      doc.rect(M, infoTop, leftW, leftH, 'S');
      drawUnderlinedBold("Site / Location", M + 4, infoTop + 8, 11, leftW - 8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(doc.splitTextToSize(String(record.siteLocation || ""), leftW - 8), M + 4, infoTop + 18);

      // ---- Engineer / Estimated time (right) ----
      const RX = M + leftW + 8;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Engineer", RX, infoTop + 8);
      const engLineX = RX + doc.getTextWidth("Engineer") + 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(String(record.engineer || ""), engLineX + 2, infoTop + 8);
      doc.setLineWidth(0.4);
      doc.line(engLineX, infoTop + 9.5, pageWidth - M, infoTop + 9.5);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Estimated time to undertake work", RX, infoTop + 24);
      const estLineX = RX + doc.getTextWidth("Estimated time to undertake work") + 4;
      doc.setFont("helvetica", "normal");
      doc.text(String(record.estimatedTime || ""), estLineX + 2, infoTop + 24);
      doc.setLineWidth(0.4);
      doc.line(estLineX, infoTop + 25.5, pageWidth - M, infoTop + 25.5);

      // ---- Date (left) + Job Reference (right) ----
      const rowY = infoTop + leftH + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Date", M, rowY);
      const dateLineX = M + doc.getTextWidth("Date") + 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(String(record.date || ""), dateLineX + 2, rowY);
      doc.setLineWidth(0.4);
      doc.line(dateLineX, rowY + 1.5, M + leftW + 5, rowY + 1.5);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Job Reference", RX, rowY);
      const jrX = RX + doc.getTextWidth("Job Reference") + 5;
      doc.setLineWidth(0.6);
      doc.rect(jrX, rowY - 6, pageWidth - M - jrX, 11, 'S');
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(String(record.jobReference || ""), jrX + 3, rowY);

      // ---- Main concerns box ----
      const boxTop = rowY + 12;
      let by = boxTop + 9;

      by = drawUnderlinedBold("Any Safety issues eg - Working at Height / Safe System of Work Required:", M + 4, by, 10);
      by += 4;
      by = drawRuledValue(record.safetyIssues, M + 4, by, contentW - 8, 4);
      by += 4;

      by = drawUnderlinedBold("Specialist Equipment Required eg - MEWP/Tower/Post Jacks/Pit Keys/Harness/Lifting Equipment:", M + 4, by, 10);
      by = drawUnderlinedBold("IF TOWER REQUIRED PLEASE ADVISE HEIGHT", M + 4, by + 1, 10);
      by += 4;
      by = drawRuledValue(record.specialistEquipment, M + 4, by, contentW - 8, 3);
      by += 4;

      by = drawUnderlinedBold("Type of Cable Route EG. Duct/Conduit/Trunking/Length of Run:", M + 4, by, 10);
      by += 4;
      by = drawRuledValue(record.cableRoute, M + 4, by, contentW - 8, 3);
      by += 4;

      by = drawUnderlinedBold("Any Other Comments:", M + 4, by, 10);
      by += 4;
      by = drawRuledValue(record.otherComments, M + 4, by, contentW - 8, 2);
      by += 6;

      by = drawYesNo("Does the work need to be done out of normal working hours", record.outOfNormalWorkingHours, M + 4, by, 10);

      // Border around everything above
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.6);
      doc.rect(M, boxTop, contentW, by - boxTop + 2, 'S');

      // =====================  PAGE 2  =====================
      doc.addPage();
      await drawHeader();

      let y2 = headerHeight + 12;

      // ---- Contractors / photographs box ----
      const box2Top = y2;
      let cy = box2Top + 10;
      cy = drawYesNo("Are Other Contractors on Site", record.otherContractorsOnSite, M + 5, cy, 10);
      cy += 3;
      cy = drawUnderlinedBold("If Yes Who/Contact Name & Number", M + 5, cy, 10);
      cy += 5;
      cy = drawRuledValue(record.contractorContactDetails, M + 5, cy, contentW - 20, 1);
      cy += 4;
      cy = drawYesNo("Have Supporting Photo's Been Taken", record.supportingPhotosTaken, M + 5, cy, 10);
      cy += 2;
      cy = drawUnderlinedBold("If photographs taken please attach / send with report", M + 5, cy, 10);

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.9);
      doc.rect(M, box2Top, contentW, cy - box2Top + 5, 'S');

      let ny = cy + 14;

      // ---- Notes ----
      ny = drawUnderlinedBold("Notes:", M, ny, 11);
      ny += 5;
      ny = drawRuledValue(record.notes, M, ny, contentW, 6);
      ny += 6;

      // ---- Materials Required ----
      ny = drawUnderlinedBold("Materials Required:", M, ny, 11);
      ny += 3;

      // Only real material rows are printed - no blank filler rows.
      const materialRows = (Array.isArray(record.materialsRequired) ? record.materialsRequired : [])
        .filter((m) => String(m?.description || "").trim() || String(m?.quantity || "").trim());
      const materialBody = materialRows.length > 0
        ? materialRows.map((m) => [ (m?.description || ""), (m?.quantity || "") ])
        : [[{ content: "No materials required.", colSpan: 2, styles: { halign: "center", textColor: [110, 110, 110], fontStyle: "italic" } }]];

      autoTable(doc, {
        startY: ny,
        columnStyles: {
          0: { cellWidth: contentW - 50 },
          1: { cellWidth: 50, halign: "center" },
        },
        head: [
          [
            { content: "DESCRIPTION", styles: { halign: "center", fontStyle: "bold" } },
            { content: "QUANTITY\n(ALL CONTAINMENT & CABLE TO BE QUOTED IN METRES)", styles: { halign: "center", fontStyle: "bold" } },
          ],
        ],
        body: materialBody,
        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: 2.5,
          overflow: "linebreak",
          valign: "middle",
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.4,
          minCellHeight: 8,
        },
        theme: "grid",
        tableWidth: contentW,
        margin: { left: M, right: M },
        headStyles: {
          fillColor: [200, 200, 200],
          textColor: [0, 0, 0],
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
        },
        bodyStyles: {
          lineWidth: 0.4,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
        },
      });

      let py = doc.lastAutoTable.finalY + 10;

      // ---- Attached supporting photographs (when photos were uploaded) ----
      const photos = Array.isArray(record.supportingPhotos) ? record.supportingPhotos.filter(Boolean) : [];
      if (photos.length > 0) {
        if (py + 50 > pageHeight - 20) {
          doc.addPage();
          py = 25;
        }
        py = drawUnderlinedBold("Supporting Photographs:", M, py, 11);
        py += 6;

        const gap = 8;
        const perRow = 2;
        const photoW = (contentW - gap) / perRow;   // 2 per row - much larger, readable
        const photoH = 62;
        for (let i = 0; i < photos.length; i++) {
          const col = i % perRow;
          if (col === 0 && i > 0) py += photoH + gap + 6;
          if (py + photoH > pageHeight - 20) {
            doc.addPage();
            py = 25;
          }
          const x = M + col * (photoW + gap);
          try {
            const src = String(photos[i]).startsWith('data:image')
              ? photos[i]
              : `data:image/png;base64,${photos[i]}`;

            // Fit the image inside the frame keeping its real aspect ratio,
            // then centre it so nothing is stretched or cropped.
            let drawW = photoW - 3;
            let drawH = photoH - 3;
            try {
              const props = doc.getImageProperties(src);
              if (props && props.width && props.height) {
                const ratio = props.width / props.height;
                drawW = photoW - 3;
                drawH = drawW / ratio;
                if (drawH > photoH - 3) {
                  drawH = photoH - 3;
                  drawW = drawH * ratio;
                }
              }
            } catch (e) { /* fall back to the full frame */ }

            const imgX = x + (photoW - drawW) / 2;
            const imgY = py + (photoH - drawH) / 2;

            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.4);
            doc.rect(x, py, photoW, photoH, 'S');
            doc.addImage(src, 'JPEG', imgX, imgY, drawW, drawH, '', 'NONE');

            // Caption under each frame
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(90, 90, 90);
            doc.text(`Photo ${i + 1}`, x + photoW / 2, py + photoH + 4.5, { align: 'center' });
          } catch (e) {
            doc.setFont("helvetica", "italic");
            doc.setFontSize(8);
            doc.setTextColor(120, 120, 120);
            doc.text("Image could not be loaded", x + 3, py + photoH / 2);
          }
        }
      }

      // ---- Footer on every page (matches the printed form's footer) ----
      const totalPages = doc.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text("TTSL ref: SQE/PRO/019J - Additional concerns worksheet for quotes v.2", M, pageHeight - 8);
        doc.text(String(p), pageWidth - M, pageHeight - 8, { align: 'right' });
      }

      // Build filename and either save or upload
      const safeName = String(record.siteLocation || 'UKPNAdditionalConcerns').replace(/[\\/:"*?<>|]+/g, '-');
      const safeDate = String(record.date || 'Report').replace(/[\\/:"*?<>|]+/g, '-');

      let fileName;
      if (shouldUploadToSharePoint && uploadConfig) {
        fileName = buildSharePointFileName([
          addedByName(record),
          uploadConfig.siteName,
          toDdMmYyyy(record.date),
        ]);
      } else {
        // For regular download, use full format with prefix and date
        fileName = `UKPNAdditionalConcerns_${safeName}_${safeDate}.pdf`;
      }

      if (shouldUploadToSharePoint && uploadConfig) {
        try {
          const key = `${record._id || ''}|${record.siteLocation || ''}|${record.date || ''}`;
          setUploadingKey(key);
          const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
          const dataUri = doc.output('datauristring');

          // Prepare SharePoint upload parameters
          const uploadParams = {
            fileName,
            fileBase64: dataUri,
            phaseType: uploadConfig.phaseType,
            phaseNumber: uploadConfig.phaseNumber,
            network: uploadConfig.network,
            siteName: uploadConfig.siteName, uploadContext: "reactive-ukpn-tablets", uploadSubFolder: "Additional Concerns Worksheet"
          };

          const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
            method: 'POST',
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: JSON.stringify(uploadParams)
          });
          if (res.status !== 204 && !res.ok) {
            let msg = 'SharePoint upload failed';
            try {
              const out = await res.json();
              msg = out?.message || msg;
            } catch (_) {}
            throw new Error(msg);
          }
          toast.success("PDF uploaded to SharePoint successfully!");
          if (uploadConfig.onSuccess) {
            uploadConfig.onSuccess();
          }
        } catch (err) {
          toast.error(err?.message || "SharePoint upload failed");
          if (uploadConfig.onError) {
            uploadConfig.onError(err);
          }
        } finally {
          setUploadingKey(null);
        }
      } else {
        doc.save(fileName);
        toast.success("Professional Additional Concerns PDF generated successfully!");
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to generate PDF');
    } finally {
      generatingPdfRef.current = false;
    }
  };

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: '20px',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
      padding: '24px',
      marginBottom: '24px'
    }}>
      {/* Header Section */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#1a202c',
          margin: 0
        }}>Additional Concerns Worksheet</h2>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <i className="fa fa-search" style={{
              position: 'absolute',
              left: '12px',
              color: '#8b1634',
              fontSize: '14px'
            }}></i>
            <input
              type="text"
              placeholder="Type to Search"
              value={search}
              onChange={e=>{ setSearch(e.target.value); setPage(1); }}
              style={{
                padding: '10px 12px 10px 36px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                outline: 'none',
                width: '250px',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8b1634'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div style={{
        overflowX: 'auto',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr 0.8fr 1fr 1fr 1fr 1.2fr',
          background: '#f7fafc',
          padding: '12px 16px',
          borderBottom: '1px solid #e2e8f0',
          gap: '12px',
          alignItems: 'center'
        }}>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Site / Location</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Engineer</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Date</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Job Reference</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
        </div>

        {/* Table Body */}
        <div>
          {paged.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#718096',
              fontSize: '16px'
            }}>No records found.</div>
          ) : paged.map((f, idx) => {
              // First check if firstName and lastName are saved in the record
              // If not, try to find user from appUsers array
              let userName = '-';
              if (f.addedByFirstName && f.addedByLastName) {
                userName = `${f.addedByFirstName} ${f.addedByLastName}`;
              } else {
              const user = appUsers.find(u => u._id === f.userId);
                userName = user ? `${user.firstName} ${user.lastName}` : (f.userId || '-');
              }
              return (
                <div
                  key={f._id || idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.2fr 1fr 0.8fr 1fr 1fr 1fr 1.2fr',
                    padding: '16px',
                    borderBottom: '1px solid #f1f5f9',
                    gap: '12px',
                    alignItems: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div style={{color: '#2d3748', fontSize: '14px'}}>{f.siteLocation || '—'}</div>
                  <div style={{color: '#2d3748', fontSize: '14px'}}>{f.engineer || '—'}</div>
                  <div style={{color: '#2d3748', fontSize: '14px'}}>{f.date || '—'}</div>
                  <div style={{color: '#2d3748', fontSize: '14px'}}>{f.jobReference || '—'}</div>
                  <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                  <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(f.updatedAt || f.createdAt || f.date)}</div>
                  <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                    {canEditDelete && (
                      <button
                        onClick={()=>handleEdit(f)}
                        title="Edit"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                      </button>
                    )}
                    <button
                      onClick={()=>generatePDF(f)}
                      title="Generate PDF"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                    </button>
                    {(() => {
                      const k = `${f._id || ''}|${f.siteLocation || ''}|${f.date || ''}`;
                      const busy = uploadingKey === k;
                      return (
                        <button
                          onClick={()=>{
                            if (!busy) {
                              setSharePointUploadRecord(f);
                              setSharePointPhaseType('phase');
                              setSharePointPhaseNumber('15');
                              setSharePointNetwork('');
                              setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                            }
                          }}
                          disabled={busy}
                          title={busy ? "Uploading..." : "Upload to SharePoint"}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: busy ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                      );
                    })()}
                    {canEditDelete && (
                      <button
                        onClick={()=>handleDelete(f._id)}
                        title="Delete"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Pagination */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: '18px',
        gap: '12px'
      }}>
        <button
          disabled={page===1 || filtered.length === 0}
          onClick={()=>setPage(page-1)}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
            color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
            cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >Prev</button>
        <span style={{
          fontWeight: '700',
          color: '#8b1634',
          fontSize: '16px'
        }}>{page} / {totalPages}</span>
        <button
          disabled={page===totalPages || filtered.length === 0}
          onClick={()=>setPage(page+1)}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
            color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
            cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >Next</button>
      </div>

      {/* Edit Modal */}
      {showEditModal && editForm && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={adminEditModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>Edit Additional Concerns Worksheet</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Site / Location</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editForm.siteLocation || ""} onChange={e=>handleEditChange('siteLocation', e.target.value)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Engineer</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editForm.engineer || ""} onChange={e=>handleEditChange('engineer', e.target.value)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Estimated Time To Undertake Work</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editForm.estimatedTime || ""} onChange={e=>handleEditChange('estimatedTime', e.target.value)} />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Date</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editForm.date)}
                        onChange={(d) => handleEditChange('date', formatSiteRegisterDate(d))}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Job Reference</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editForm.jobReference || ""} onChange={e=>handleEditChange('jobReference', e.target.value)} />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12">
                    <label style={adminEditLabelStyle}>Any Safety Issues eg – Working at Height / Safe System of Work Required</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      style={{ ...adminEditInputStyle, height: "auto", minHeight: "100px", resize: "vertical" }}
                      value={editForm.safetyIssues || ""}
                      onChange={e=>handleEditChange('safetyIssues', e.target.value)}
                    />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12">
                    <label style={adminEditLabelStyle}>Specialist Equipment Required eg – MEWP/Tower/Post Jacks/Pit Keys/Harness/Lifting Equipment (IF TOWER REQUIRED PLEASE ADVISE HEIGHT)</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      style={{ ...adminEditInputStyle, height: "auto", minHeight: "100px", resize: "vertical" }}
                      value={editForm.specialistEquipment || ""}
                      onChange={e=>handleEditChange('specialistEquipment', e.target.value)}
                    />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12">
                    <label style={adminEditLabelStyle}>Type of Cable Route EG. Duct/Conduit/Trunking/Length of Run</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      style={{ ...adminEditInputStyle, height: "auto", minHeight: "100px", resize: "vertical" }}
                      value={editForm.cableRoute || ""}
                      onChange={e=>handleEditChange('cableRoute', e.target.value)}
                    />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12">
                    <label style={adminEditLabelStyle}>Any Other Comments</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      style={{ ...adminEditInputStyle, height: "auto", minHeight: "80px", resize: "vertical" }}
                      value={editForm.otherComments || ""}
                      onChange={e=>handleEditChange('otherComments', e.target.value)}
                    />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Work Out Of Normal Working Hours</label>
                    <select className="form-control" style={adminEditInputStyle} value={editForm.outOfNormalWorkingHours || ""} onChange={e=>handleEditChange('outOfNormalWorkingHours', e.target.value)}>
                      <option value="">Select</option>
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Are Other Contractors On Site</label>
                    <select className="form-control" style={adminEditInputStyle} value={editForm.otherContractorsOnSite || ""} onChange={e=>handleEditChange('otherContractorsOnSite', e.target.value)}>
                      <option value="">Select</option>
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Have Supporting Photo's Been Taken</label>
                    <select className="form-control" style={adminEditInputStyle} value={editForm.supportingPhotosTaken || ""} onChange={e=>handleEditChange('supportingPhotosTaken', e.target.value)}>
                      <option value="">Select</option>
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>
                </div>
                {editForm.otherContractorsOnSite === 'YES' && (
                  <div className="row g-3 mt-1">
                    <div className="col-12">
                      <label style={adminEditLabelStyle}>If Yes Who / Contact Name &amp; Number</label>
                      <input type="text" className="form-control" style={adminEditInputStyle} value={editForm.contractorContactDetails || ""} onChange={e=>handleEditChange('contractorContactDetails', e.target.value)} />
                    </div>
                  </div>
                )}

                {editForm.supportingPhotosTaken === 'YES' && (
                  <div className="row g-3 mt-1">
                    <div className="col-12">
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px', flexWrap:'wrap', gap:'8px'}}>
                        <label style={{...adminEditLabelStyle, marginBottom: 0}}>
                          Supporting Photographs
                          <span style={{marginLeft:'8px', background:'#e9cfd6', color:'#8b1634', borderRadius:'10px', padding:'2px 8px', fontSize:'12px', fontWeight:600}}>
                            {(editForm.supportingPhotos || []).length}
                          </span>
                        </label>
                        <label
                          style={{background:'#8b1634', color:'#fff', border:'none', borderRadius:'8px', padding:'6px 12px', fontSize:'13px', fontWeight:600, cursor:'pointer', marginBottom:0}}
                        >
                          <i className="fa fa-upload" style={{marginRight:'6px'}}></i>
                          Upload Photos
                          <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} style={{display:'none'}} />
                        </label>
                      </div>
                      <div style={{border:'1px solid #e2e8f0', borderRadius:'10px', padding:'12px'}}>
                        {(editForm.supportingPhotos || []).length === 0 ? (
                          <div style={{padding:'18px', textAlign:'center', color:'#718096', fontSize:'14px'}}>
                            No photographs uploaded.
                          </div>
                        ) : (
                          <div style={{display:'flex', flexWrap:'wrap', gap:'12px'}}>
                            {(editForm.supportingPhotos || []).map((src, i) => (
                              <div key={i} style={{position:'relative', width:'130px'}}>
                                <img
                                  src={src.startsWith('data:image') ? src : `data:image/png;base64,${src}`}
                                  alt={`Supporting ${i + 1}`}
                                  style={{
                                    width:'130px',
                                    height:'100px',
                                    objectFit:'cover',
                                    border:'1px solid #ddd',
                                    borderRadius:'8px',
                                    backgroundColor:'white',
                                  }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <button
                                  type="button"
                                  onClick={()=>removePhoto(i)}
                                  title="Remove"
                                  style={{
                                    position:'absolute',
                                    top:'-8px',
                                    right:'-8px',
                                    background:'#e53e3e',
                                    color:'#fff',
                                    border:'none',
                                    borderRadius:'50%',
                                    width:'24px',
                                    height:'24px',
                                    cursor:'pointer',
                                    fontSize:'12px',
                                    lineHeight:'24px',
                                    padding:0,
                                  }}
                                >
                                  <i className="fa fa-times"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="row g-3 mt-1">
                  <div className="col-12">
                    <label style={adminEditLabelStyle}>Notes</label>
                    <textarea
                      className="form-control"
                      rows={5}
                      style={{ ...adminEditInputStyle, height: "auto", minHeight: "120px", resize: "vertical" }}
                      value={editForm.notes || ""}
                      onChange={e=>handleEditChange('notes', e.target.value)}
                    />
                  </div>
                </div>

                {/* Materials Required */}
                <div className="row g-3 mt-1">
                  <div className="col-12">
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
                      <label style={{...adminEditLabelStyle, marginBottom: 0}}>
                        Materials Required
                        <span style={{marginLeft:'8px', background:'#e9cfd6', color:'#8b1634', borderRadius:'10px', padding:'2px 8px', fontSize:'12px', fontWeight:600}}>
                          {(editForm.materialsRequired || []).length}
                        </span>
                      </label>
                      <button
                        type="button"
                        onClick={addMaterialRow}
                        style={{background:'#8b1634', color:'#fff', border:'none', borderRadius:'8px', padding:'6px 12px', fontSize:'13px', fontWeight:600, cursor:'pointer'}}
                      >
                        Add Row
                      </button>
                    </div>
                    <div style={{border:'1px solid #e2e8f0', borderRadius:'10px', overflow:'hidden'}}>
                      <div style={{display:'grid', gridTemplateColumns:'1fr 200px 60px', gap:'10px', background:'#f7fafc', padding:'10px 12px', borderBottom:'1px solid #e2e8f0'}}>
                        <div style={{fontWeight:600, color:'#8b1634', fontSize:'13px'}}>Description</div>
                        <div style={{fontWeight:600, color:'#8b1634', fontSize:'13px'}}>Quantity (metres)</div>
                        <div></div>
                      </div>
                      {(editForm.materialsRequired || []).length === 0 ? (
                        <div style={{padding:'18px', textAlign:'center', color:'#718096', fontSize:'14px'}}>No materials added.</div>
                      ) : (editForm.materialsRequired || []).map((m, i) => (
                        <div key={i} style={{display:'grid', gridTemplateColumns:'1fr 200px 60px', gap:'10px', padding:'10px 12px', borderBottom:'1px solid #f1f5f9', alignItems:'center'}}>
                          <input type="text" className="form-control" style={adminEditInputStyle} value={m?.description || ""} onChange={e=>updateMaterialRow(i, 'description', e.target.value)} />
                          <input type="text" className="form-control" style={adminEditInputStyle} value={m?.quantity || ""} onChange={e=>updateMaterialRow(i, 'quantity', e.target.value)} />
                          <button
                            type="button"
                            onClick={()=>removeMaterialRow(i)}
                            title="Remove"
                            style={{background:'transparent', border:'none', cursor:'pointer', color:'#e53e3e', fontSize:'16px'}}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select
                      className="form-select"
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>

                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select
                          className="form-select"
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select
                          className="form-select"
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}

                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Additional Concerns Worksheet</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UkpnAdditionalConcernsPage;
